import hubSpotPlugin from '@analytics/hubspot'
import googleTagManager from '@analytics/google-tag-manager'
import { attributionPlugin } from '@vendia/analytics-attribution'
import Analytics from 'analytics'
import debug from 'debug'

import config from '../../config'
import enrichHubSpotPayload from './plugins/enrich-hubspot'
/* local plugins */
import eventPrefixerPlugin from './plugins/event-prefixer'
import eventValidationPlugin from './plugins/event-validation'
import loggerPlugin from './plugins/logger'
import pendoPlugin from './plugins/pendo'
import posthogPlugin from './plugins/posthog'
import { getRefData } from './utils/get-ref-data'

const { isLocalHost, isBrowser, isInternalUser, isProd, isStaging, isDev } = config
const logger = debug('analytics:init')

const APP_VERSION = '0.0.1'
// If we are in browser and (stage === staging) OR (stage === prod) + is prod domain
// Never load analytics plugins for internal users (?internal=true)
const shouldLoadPlugins = isBrowser && !isLocalHost && !isInternalUser && (isProd || isStaging)

/* Telemetry events */
const EVENTS = {
  /* Global events */
  // page view
  PAGE_VIEW: 'global.page_viewed',
  // form conversion
  FORM_SUBMITTED: 'global.form_submitted',

  /* User specific events */
  // User sign up
  USER_SIGN_UP: 'app.user_signUp',
  // User sign in
  USER_SIGN_IN: 'app.user_signIn',
  // User sign out
  USER_SIGN_OUT: 'app.user_signOut',
  // User reset their password
  USER_PASSWORD_RESET: 'app.user_passwordReset',
  // Error with user login
  USER_AUTH_ERROR: 'app.user_authError',
  // User requested change email
  USER_EMAIL_CHANGE_START: 'app.user_emailChangeRequested',
  // User completed changing their email
  USER_EMAIL_CHANGE_COMPLETED: 'app.user_emailChangeConfirmed',
  // User verified email
  USER_EMAIL_CONFIRMED: 'app.user_emailVerified',
  // User has enabled 2 factor auth
  USER_2FA_ENABLED: 'app.user_2faEnabled',
  // User has disable 2 factor auth
  USER_2FA_DISABLED: 'app.user_2faDisabled',
  // User deactivated
  USER_DEACTIVATED: 'app.user_deactivated',
  // User completed the additional MCP qualification info
  USER_QUALIFICATION_INFO_COMPLETED: 'app.user_qualificationInfoCompleted',

  /* Role specific events */
  // Role created
  ROLE_CREATED: 'app.role_created',
  // Role updated
  ROLE_UPDATED: 'app.role_updated',
  // Role deactiavted
  ROLE_DEACTIVATED: 'app.role_deactivated',

  /* Uni specific events */
  // User created a new uni
  UNI_CREATED: 'app.uni_created',
  // User attempted to create new uni but it failed
  UNI_CREATE_FAILED: 'app.uni_createFailed',
  //User invited partner
  UNI_INVITE: 'app.uni_invite',
  /* @TODO
  UNI_DESTROYED: 'app:uni_destroyed',
  UNI_DESTROY_FAILED: 'app.uni_destroyFailed',
  UNI_INVITE_FAILED: 'app.uni_inviteFailed',
  UNI_JOIN: 'app.uni_join',
  UNI_JOIN_FAILED: 'app.uni_joinFailed',
  */

  /* Uni Node specific events */
  NODE_SETTINGS_UPDATED: 'app.node_settingsUpdated',
  NODE_ARCHIVED: 'app.node_archived',
  NODE_DELETED: 'app.node_deleted',

  /* MCP specific events */
  MCP_URL_COPIED: 'app.mcp_urlCopied',
  MCP_DEMO_PROMPT_COPIED: 'app.mcp_demoPromptCopied',
  MCP_DASHBOARD_VIEWED: 'app.mcp_dashboardViewed',

  /* Storage Connection specific events */
  STORAGE_CONNECTION_ADD_CLICKED: 'app.storageConnection_addClicked',
  STORAGE_CONNECTION_CREATED: 'app.storageConnection_created',
  STORAGE_CONNECTION_CREATE_FAILED: 'app.storageConnection_createFailed',
  STORAGE_CONNECTION_ACCESS_POLICY_EDITED: 'app.storageConnection_accessPolicyEdited',
  STORAGE_CONNECTION_ACCESS_POLICY_SAVE_FAILED: 'app.storageConnection_accessPolicySaveFailed',

  /* API Connection specific events */
  API_CATALOG_ADD_CLICKED: 'app.apiCatalog_addClicked',
  API_CATALOG_CREATED: 'app.apiCatalog_created',
  API_CATALOG_CREATE_FAILED: 'app.apiCatalog_createFailed',

  /* API Connection Access Policy events */
  API_CONNECTION_ACCESS_POLICY_CREATED: 'app.apiConnection_accessPolicyCreated',
  API_CONNECTION_ACCESS_POLICY_EDITED: 'app.apiConnection_accessPolicyEdited',
  API_CONNECTION_ACCESS_POLICY_DELETED: 'app.apiConnection_accessPolicyDeleted',
  API_CONNECTION_ACCESS_POLICY_HEADERS_UPDATED: 'app.apiConnection_accessPolicyHeadersUpdated',

  /* Gateway specific events */
  GATEWAY_ADD_CLICKED: 'app.gateway_addClicked',
  GATEWAY_CREATED: 'app.gateway_created',
  GATEWAY_CREATE_FAILED: 'app.gateway_createFailed',
  GATEWAY_UPDATED: 'app.gateway_updated',
  GATEWAY_UPDATE_FAILED: 'app.gateway_updateFailed',

  /* Organization member events */
  ORG_MEMBER_INVITE_SENT: 'app.orgMember_inviteSent',
  ORG_MEMBER_INVITE_FAILED: 'app.orgMember_inviteFailed',

  /* Analyze/Observe page events */
  ANALYZE_RECEIPTS_FILTER_CHANGED: 'app.analyze_receiptsFilterChanged',
  ANALYZE_TRANSACTIONS_FILTER_CHANGED: 'app.analyze_transactionsFilterChanged',

  /* Vendia Table specific events */
  TABLE_ADD_CLICKED: 'app.table_addClicked',
  TABLE_CREATED: 'app.table_created',
  TABLE_CREATE_FAILED: 'app.table_createFailed',

  /* Data Product specific events */
  DATA_PRODUCT_ADD_CLICKED: 'app.dataProduct_addClicked',
  DATA_PRODUCT_CREATED: 'app.dataProduct_created',
  DATA_PRODUCT_CREATE_FAILED: 'app.dataProduct_createFailed',

  /* Schema Evolution specific events */
  SCHEMA_EVOLUTION_INITIATED: 'app.schemaEvolution_initiated',
  SCHEMA_EVOLUTION_FAILED: 'app.schemaEvolution_failed',
  /* Schema Promotion specific events */
  SCHEMA_PROMOTION_INITIATED: 'app.schemaPromotion_initiated',
  SCHEMA_PROMOTION_FAILED: 'app.schemaPromotion_failed',

  /* External link events */
  EXTERNAL_LINK_CLICKED: 'app.externalLink_clicked',

  /* Enterprise modal events */
  ENTERPRISE_MODAL_VIEWED: 'app.enterpriseModal_viewed',

  /* BILLING EVENTS */
  BILLING_PLAN_SELECTED: 'app.billing_planSelected',
  BILLING_SELECT_UNIS_SUBMITTED: 'app.billing_selectUnisSubmitted',
  BILLING_ADDRESS_SUBMITTED: 'app.billing_addressSubmitted',
  BILLING_PAYMENT_SUBMITTED: 'app.billing_paymentSubmitted',
  BILLING_CONTACT_FORM_SUBMITTED: 'app.billing_contactFormSubmitted',
  BILLING_PAYMENT_SUCCESS: 'app.billing_paymentSuccess',
  // Changing sub:
  BILLING_CHANGE_UNIS_SUBMITTED: 'app.billing_changeUnisSubmitted',
  BILLING_CHANGE_UNIS_SUCCESS: 'app.billing_changeUnisSuccess',
  // Cancelling sub:
  BILLING_CANCEL_SUBMITTED: 'app.billing_cancelSubmitted',
  // Any error along the way:
  BILLING_ERROR: 'app.billing_error',
}

const pageKey = 'app.pageViewCount'
const sessionKey = 'app.sessionCount'

/* Analytic plugins */
const plugins = [
  /* Save initial landing page & referrer */
  attributionPlugin(),
  /* Automatically prefix all event names */
  eventPrefixerPlugin(['app.', 'global.'], 'app.'),
  /* Validate all outgoing requests */
  eventValidationPlugin({
    // Namespace of current application
    context: ['app', 'global'],
    // Allowed objects
    objects: [
      'test',
      'uni', // example app.uni_created, app.uni_destroyed
      'node', // example app.node_created, app.node_destroyed, app.node_settingsUpdated
      'user', // example app.user_signup, app.user_login
      'page', // example global.page_viewed
      'form', // example global.form_submitted
      'billing', // example app.billing_planSelected
      'role', //example app.role_created
      'mcp', // example app.mcp_urlCopied, app.mcp_dashboardViewed
      'storageConnection', // example app.storageConnection_created
      'apiCatalog', // example app.apiCatalog_created
      'apiConnection', // example app.apiConnection_accessPolicyCreated
      'gateway', // example app.gateway_created
      'orgMember', // example app.orgMember_inviteSent
      'analyze', // example app.analyze_receiptsFilterChanged
      'table', // example app.table_created
      'dataProduct', // example app.dataProduct_created
      'schemaEvolution', // example app.schemaEvolution_initiated
      'schemaPromotion', // example app.schemaPromotion_initiated
      'externalLink', // example app.externalLink_clicked
      'enterpriseModal', // example app.enterpriseModal_viewed
    ],
  }),
  /* Automatically format payloads for HubSpot */
  enrichHubSpotPayload(),
  /* Load analytics logger. Logs only if localStorage.debug set */
  loggerPlugin,
  /* Load Analytic endpoints */
  ...(!shouldLoadPlugins
    ? []
    : [
        /* Load Google Tag manager */
        googleTagManager({
          containerId: 'GTM-MLTN2M3',
        }),
        /* Load HubSpot */
        hubSpotPlugin({
          portalId: config.hubspotId,
          // Hubspot's tracking code requires a page view to be fired for the identify information to be sent
          flushOnIdentify: true,
        }),
        /* Load Pendo */
        pendoPlugin,
        /* Load PostHog */
        posthogPlugin,
      ]),
]

logger('plugins', plugins)

/* Initialize analytics instance */
const analytics = Analytics({
  app: 'vendia',
  version: APP_VERSION,
  debug: isDev,
  plugins: plugins,
})

/* expose analytics to window for debugging. @TODO remove */
if (isBrowser) {
  window.analytics = analytics
}

/* Check referral data */
getRefData().then((leadSrc) => {
  logger('referral', leadSrc)
})

/* export analytics instance for usage in app */
export default analytics

/* export analytics event constants for usage in app */
export { EVENTS }
